import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API_ENDPOINTS } from "../../config/api";
import { useAuth } from "../../context/AuthContext";
import SearchFilter from "../../components/Common/SearchFilter";
import PropertyFormModal from "../../components/Property/PropertyFormModal";
import UnitFormModal from "../../components/Property/UnitFormModal";
import PropertyImagePlaceholder from "../../components/Common/PropertyImagePlaceholder";

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  page: {
    minHeight: "100vh",
    background: "#F4F5F7",
    padding: "2rem 1.75rem",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    color: "#1a1d23",
  },
  pageTitle: { fontSize: 20, fontWeight: 600, color: "#1a1d23", marginBottom: 3 },
  pageSub: { fontSize: 13, color: "#6b7280" },
  sectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: "#6b7280",
  },
  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    overflow: "hidden",
  },
  cardHead: {
    padding: "0.75rem 1rem",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "#fafafa",
  },
  // Buttons
  addBtn: {
    fontSize: 13,
    fontWeight: 600,
    padding: "7px 18px",
    borderRadius: 8,
    border: "none",
    background: "#1E40AF",
    color: "#fff",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  actBtn: {
    fontSize: 11,
    fontWeight: 500,
    padding: "5px 12px",
    borderRadius: 20,
    border: "1px solid #d1d5db",
    background: "transparent",
    color: "#374151",
    cursor: "pointer",
    width: "100%",
    textAlign: "center",
  },
  actBtnSm: {
    fontSize: 11,
    fontWeight: 500,
    padding: "4px 12px",
    borderRadius: 20,
    border: "1px solid #d1d5db",
    background: "transparent",
    color: "#374151",
    cursor: "pointer",
  },
  clearBtn: {
    fontSize: 12,
    color: "#6b7280",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
  },
  // Segmented control
  seg: {
    display: "flex",
    background: "#f3f4f6",
    border: "1px solid #e5e7eb",
    borderRadius: 7,
    padding: 2,
    gap: 2,
  },
  segBtn: (active) => ({
    fontSize: 11,
    fontWeight: 500,
    padding: "4px 12px",
    borderRadius: 5,
    border: active ? "1px solid #e5e7eb" : "1px solid transparent",
    background: active ? "#fff" : "transparent",
    color: active ? "#1a1d23" : "#6b7280",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 5,
  }),
  // Pills
  pill: (variant) => {
    const map = {
      blue: { background: "#EFF6FF", color: "#1E40AF" },
      teal: { background: "#F0FDFA", color: "#115E59" },
      green: { background: "#F0FDF4", color: "#166534" },
      amber: { background: "#FFFBEB", color: "#92400E" },
      red: { background: "#FEF2F2", color: "#991B1B" },
      purple: { background: "#F5F3FF", color: "#5B21B6" },
      gray: { background: "#F9FAFB", color: "#4B5563", border: "1px solid #e5e7eb" },
    };
    return {
      fontSize: 11,
      fontWeight: 600,
      padding: "2px 9px",
      borderRadius: 20,
      whiteSpace: "nowrap",
      display: "inline-block",
      ...(map[variant] || map.gray),
    };
  },
  // Table
  th: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#6b7280",
    padding: "8px 12px",
    textAlign: "left",
    borderBottom: "1px solid #e5e7eb",
    whiteSpace: "nowrap",
    background: "#fafafa",
  },
  td: {
    padding: "9px 12px",
    borderBottom: "1px solid #f3f4f6",
    color: "#1a1d23",
    fontSize: 13,
    verticalAlign: "middle",
  },
  // Spinner
  spinner: {
    display: "inline-block",
    width: 18,
    height: 18,
    border: "2px solid #e5e7eb",
    borderTopColor: "#6b7280",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
    verticalAlign: "middle",
    marginRight: 8,
  },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatPrice = (price) =>
  new Intl.NumberFormat("en-TT", {
    style: "currency",
    currency: "TTD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);

const typePillVariant = (type) => {
  switch (type) {
    case "one_time_rental":
      return "blue";
    case "property_management":
      return "teal";
    case "sale":
      return "red";
    case "multi_unit_building":
      return "purple";
    default:
      return "gray";
  }
};

const typeLabel = (type) => {
  switch (type) {
    case "one_time_rental":
      return "One-time";
    case "property_management":
      return "Managed";
    case "sale":
      return "Sale";
    case "multi_unit_building":
      return "Building";
    default:
      return type;
  }
};

const statusPillVariant = (status) => {
  switch (status) {
    case "available":
      return "green";
    case "active":
      return "green";
    case "rented":
      return "amber";
    case "pending":
      return "amber";
    case "under_contract":
      return "purple";
    case "sold":
      return "gray";
    case "withdrawn":
    case "inactive":
    case "terminated":
      return "gray";
    default:
      return "gray";
  }
};

const statusLabel = (status) =>
  status ? status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Unknown";

// ── Sub-components ────────────────────────────────────────────────────────────
const Spinner = () => (
  <>
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    <span style={s.spinner} />
  </>
);

const Pill = ({ variant, children }) => <span style={s.pill(variant)}>{children}</span>;

const GridIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" />
    <rect x="14" y="14" width="7" height="7" />
  </svg>
);

const TableIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const PlusIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const NoImage = ({ height = 140, small = false }) => (
  <PropertyImagePlaceholder height={height} small={small} />
);

// ── Property grid card ────────────────────────────────────────────────────────
const PropertyCard = ({ property, onViewDetails }) => {
  const isRental = property.type === "one_time_rental" || property.type === "property_management";
  const isSale = property.type === "sale";
  const isBuilding = property.type === "multi_unit_building";
  const primaryImage =
    property.images?.find((img) => img.isPrimary)?.url || property.images?.[0]?.url;

  return (
    <div
      style={{ ...s.card, cursor: "pointer", transition: "transform 0.15s ease" }}
      onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-2px)")}
      onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
    >
      {/* Image area */}
      <div style={{ position: "relative" }}>
        {primaryImage ? (
          <img
            src={primaryImage}
            alt={property.title}
            style={{ width: "100%", height: 140, objectFit: "cover", display: "block" }}
          />
        ) : (
          <NoImage height={140} />
        )}
        {/* Status + type pills */}
        <div
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          <Pill variant={statusPillVariant(property.status)}>{statusLabel(property.status)}</Pill>
          <Pill variant={typePillVariant(property.type)}>{typeLabel(property.type)}</Pill>
        </div>
      </div>

      {/* Card body */}
      <div style={{ padding: "0.875rem" }}>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            marginBottom: 3,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {property.title || "Untitled property"}
        </div>
        <div style={{ fontSize: 12, color: "#6b7280", marginBottom: "0.75rem" }}>
          {[property.address?.street, property.address?.city, property.address?.parish]
            .filter(Boolean)
            .join(", ")}
        </div>

        {/* Bed / bath / sqft strip */}
        {!isBuilding && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 4,
              borderTop: "1px solid #f3f4f6",
              borderBottom: "1px solid #f3f4f6",
              padding: "6px 0",
              marginBottom: "0.75rem",
              textAlign: "center",
            }}
          >
            {[
              { val: property.propertyDetails?.bedrooms ?? 0, label: "bed" },
              { val: property.propertyDetails?.bathrooms ?? 0, label: "bath" },
              { val: property.propertyDetails?.squareFootage ?? 0, label: "sqft" },
            ].map(({ val, label }) => (
              <div key={label}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{val}</div>
                <div style={{ ...s.sectionLabel, fontSize: 10 }}>{label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Price */}
        <div style={{ marginBottom: "0.75rem" }}>
          {isRental && property.pricing?.rent && (
            <div>
              <span style={{ fontSize: 15, fontWeight: 700, color: "#1E40AF" }}>
                {formatPrice(property.pricing.rent)}
              </span>
              <span style={{ fontSize: 11, color: "#9ca3af" }}>/mo</span>
            </div>
          )}
          {isSale && property.pricing?.salePrice && (
            <span style={{ fontSize: 15, fontWeight: 700, color: "#1E40AF" }}>
              {formatPrice(property.pricing.salePrice)}
            </span>
          )}
          {isBuilding && (
            <span style={{ fontSize: 12, color: "#9ca3af" }}>Multi-unit building</span>
          )}
        </div>

        <button style={s.actBtn} onClick={() => onViewDetails(property)}>
          View details →
        </button>
      </div>
    </div>
  );
};

// ── Filter fields config ──────────────────────────────────────────────────────
const FILTER_CONFIG = {
  searchPlaceholder: "Search by title, address, city…",
  title: "Search & filters",
  filterFields: [
    {
      name: "type",
      type: "select",
      placeholder: "All types",
      options: [
        { value: "one_time_rental", label: "One-time rental" },
        { value: "property_management", label: "Property management" },
        { value: "sale", label: "Sale" },
      ],
    },
    {
      name: "status",
      type: "select",
      placeholder: "All statuses",
      options: [
        { value: "available", label: "Available" },
        { value: "pending", label: "Pending" },
        { value: "rented", label: "Rented" },
        { value: "withdrawn", label: "Withdrawn" },
        { value: "under_contract", label: "Under contract" },
        { value: "sold", label: "Sold" },
        { value: "inactive", label: "Inactive" },
        { value: "terminated", label: "Terminated" },
        { value: "active", label: "Active" },
      ],
    },
    {
      name: "propertyType",
      type: "select",
      placeholder: "Property type",
      options: [
        { value: "house", label: "House" },
        { value: "apartment", label: "Apartment" },
        { value: "condo", label: "Condo" },
        { value: "townhouse", label: "Townhouse" },
        { value: "duplex", label: "Duplex" },
        { value: "commercial", label: "Commercial" },
      ],
    },
    {
      name: "bedrooms",
      type: "select",
      placeholder: "Bedrooms",
      options: ["1", "2", "3", "4", "5"].map((v) => ({ value: v, label: `${v}+` })),
    },
    {
      name: "bathrooms",
      type: "select",
      placeholder: "Bathrooms",
      options: ["1", "2", "3", "4"].map((v) => ({ value: v, label: `${v}+` })),
    },
    { name: "minPrice", type: "number", placeholder: "Min price" },
    { name: "maxPrice", type: "number", placeholder: "Max price" },
  ],
};

const EMPTY_FILTERS = {
  type: "",
  status: "",
  minPrice: "",
  maxPrice: "",
  bedrooms: "",
  bathrooms: "",
  propertyType: "",
};

// ── Main component ────────────────────────────────────────────────────────────
function Properties() {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();

  const [properties, setProperties] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState("grid");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [createdPropertyId, setCreatedPropertyId] = useState(null);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchProperties = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(API_ENDPOINTS.properties.getAll, {
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        },
      });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json();
      setProperties(data.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  // ── Filter ────────────────────────────────────────────────────────────────
  useEffect(() => {
    let result = properties;

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      result = result.filter(
        (p) =>
          p.title?.toLowerCase().includes(q) ||
          p.address?.street?.toLowerCase().includes(q) ||
          p.address?.city?.toLowerCase().includes(q) ||
          p.address?.state?.toLowerCase().includes(q)
      );
    }
    if (filters.type) result = result.filter((p) => p.type === filters.type);
    if (filters.status) result = result.filter((p) => p.status === filters.status);
    if (filters.propertyType)
      result = result.filter((p) => p.propertyDetails?.propertyType === filters.propertyType);
    if (filters.bedrooms)
      result = result.filter(
        (p) => (p.propertyDetails?.bedrooms ?? 0) >= parseInt(filters.bedrooms)
      );
    if (filters.bathrooms)
      result = result.filter(
        (p) => (p.propertyDetails?.bathrooms ?? 0) >= parseInt(filters.bathrooms)
      );

    if (filters.minPrice || filters.maxPrice) {
      result = result.filter((p) => {
        const price = p.type === "sale" ? p.pricing?.salePrice : p.pricing?.rent;
        if (!price) return false;
        const min = filters.minPrice ? parseInt(filters.minPrice) : 0;
        const max = filters.maxPrice ? parseInt(filters.maxPrice) : Infinity;
        return price >= min && price <= max;
      });
    }

    setFilteredProperties(result);
  }, [properties, searchTerm, filters]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleFilterChange = (name, value) => setFilters((prev) => ({ ...prev, [name]: value }));

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSearchTerm("");
  };

  const handleViewDetails = (property) => {
    if (property.type === "multi_unit_building") {
      navigate(`/properties/${property._id}/units`);
    } else {
      navigate(`/properties/propertyDetails/${property._id}`);
    }
  };

  const handlePropertyCreated = (newProperty, isCreate, isMultiUnitCreation) => {
    if (isCreate) {
      setProperties((prev) => [newProperty, ...prev]);
      if (isMultiUnitCreation) {
        setCreatedPropertyId(newProperty._id);
        setShowUnitModal(true);
      }
    }
    setShowCreateModal(false);
  };

  const handleUnitCreated = (newUnit) => {
    console.log("Unit created:", newUnit);
  };
  const handleAddAnotherUnit = (unit) => {
    console.log("Adding another unit:", unit);
  };
  const handleUnitModalClose = () => {
    setShowUnitModal(false);
    if (createdPropertyId) {
      navigate(`/properties/${createdPropertyId}/units`);
      setCreatedPropertyId(null);
    }
  };

  // ── Loading / error states ────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ ...s.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div style={{ textAlign: "center" }}>
          <Spinner />
          <span style={{ fontSize: 14, color: "#6b7280" }}>Loading properties…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={s.page}>
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        <div
          style={{
            background: "#FEF2F2",
            border: "1px solid #FECACA",
            borderRadius: 10,
            padding: "1rem 1.25rem",
            color: "#991B1B",
            fontSize: 14,
          }}
        >
          Error loading properties: {error}
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={s.page}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* ── Header ── */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          marginBottom: "1.5rem",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div style={s.pageTitle}>Properties</div>
          <div style={s.pageSub}>Manage your property portfolio</div>
        </div>
        <div style={{ flexShrink: 0 }}>
          <button style={s.addBtn} onClick={() => setShowCreateModal(true)}>
            <PlusIcon /> Add property
          </button>
        </div>
      </div>

      {/* ── Search & Filters ── */}
      <div style={{ ...s.card, marginBottom: "1.25rem" }}>
        <div style={s.cardHead}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>Search &amp; filters</div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {showFilters && (
              <button style={s.clearBtn} onClick={clearFilters}>
                Clear all
              </button>
            )}
            <button
              style={{
                fontSize: 12,
                fontWeight: 500,
                padding: "4px 12px",
                borderRadius: 6,
                border: "1px solid #e5e7eb",
                background: showFilters ? "#1E40AF" : "#fff",
                color: showFilters ? "#fff" : "#374151",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 5,
              }}
              onClick={() => setShowFilters((v) => !v)}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
              >
                <line x1="4" y1="6" x2="20" y2="6" />
                <line x1="8" y1="12" x2="16" y2="12" />
                <line x1="11" y1="18" x2="13" y2="18" />
              </svg>
              {showFilters ? "Hide filters" : "Show filters"}
            </button>
          </div>
        </div>
        {showFilters && (
          <div style={{ padding: "0.875rem 1rem" }}>
            <SearchFilter
              searchTerm={searchTerm}
              filters={filters}
              onSearchChange={setSearchTerm}
              onFilterChange={handleFilterChange}
              onClearFilters={clearFilters}
              config={FILTER_CONFIG}
            />
          </div>
        )}
      </div>

      {/* ── Results bar ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "1rem",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <div style={{ fontSize: 13, color: "#6b7280" }}>
          <strong style={{ color: "#1a1d23" }}>{filteredProperties.length}</strong>{" "}
          {filteredProperties.length === 1 ? "property" : "properties"} found
        </div>
        <div style={s.seg}>
          <button style={s.segBtn(viewMode === "grid")} onClick={() => setViewMode("grid")}>
            <GridIcon /> Grid
          </button>
          <button style={s.segBtn(viewMode === "table")} onClick={() => setViewMode("table")}>
            <TableIcon /> Table
          </button>
        </div>
      </div>

      {/* ── Empty state ── */}
      {filteredProperties.length === 0 ? (
        <div
          style={{
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 12,
            padding: "3rem",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>No properties found</div>
          <div style={{ fontSize: 13, color: "#6b7280" }}>
            Try adjusting your search or filters.
          </div>
        </div>
      ) : viewMode === "grid" ? (
        // ── Grid view ──
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))",
            gap: 12,
            marginBottom: "1.5rem",
          }}
        >
          {filteredProperties.map((property) => (
            <PropertyCard
              key={property._id}
              property={property}
              onViewDetails={handleViewDetails}
            />
          ))}
        </div>
      ) : (
        // ── Table view ──
        <div style={{ ...s.card, marginBottom: "1.5rem" }}>
          <div style={{ overflowX: "auto" }}>
            <table
              className="re-table re-table-left"
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 13,
                tableLayout: "fixed",
              }}
            >
              <colgroup>
                <col style={{ width: 52 }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "11%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: 48 }} />
                <col style={{ width: 48 }} />
                <col style={{ width: 64 }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: 80 }} />
              </colgroup>
              <thead>
                <tr>
                  {[
                    { label: "Image", align: "left" },
                    { label: "Title", align: "left" },
                    { label: "Address", align: "left" },
                    { label: "Type", align: "left" },
                    { label: "Status", align: "left" },
                    { label: "Bed", align: "left" },
                    { label: "Bath", align: "left" },
                    { label: "Sqft", align: "left" },
                    { label: "Price", align: "left" },
                    { label: "", align: "right" },
                  ].map(({ label, align }, i) => (
                    <th key={i} style={{ ...s.th, textAlign: align }}>
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredProperties.map((property) => {
                  const primaryImage =
                    property.images?.find((img) => img.isPrimary)?.url || property.images?.[0]?.url;
                  const isRental =
                    property.type === "one_time_rental" || property.type === "property_management";
                  const isSale = property.type === "sale";
                  const isBuilding = property.type === "multi_unit_building";

                  return (
                    <tr
                      key={property._id}
                      style={{ cursor: "pointer" }}
                      onClick={() => handleViewDetails(property)}
                    >
                      <td data-label="Image" style={s.td}>
                        {primaryImage ? (
                          <img
                            src={primaryImage}
                            alt={property.title || "Property"}
                            style={{
                              width: 44,
                              height: 44,
                              objectFit: "cover",
                              borderRadius: 6,
                              display: "block",
                            }}
                          />
                        ) : (
                          <PropertyImagePlaceholder
                            width={44}
                            height={44}
                            borderRadius={6}
                            small
                          />
                        )}
                      </td>
                      <td data-label="Title" data-stacked="true" style={s.td}>
                        <div
                          style={{
                            fontWeight: 600,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {property.title || "—"}
                        </div>
                        {property.buildingName && (
                          <div style={{ fontSize: 11, color: "#9ca3af" }}>
                            {property.buildingName}
                          </div>
                        )}
                      </td>
                      <td data-label="Address" data-stacked="true" style={s.td}>
                        <div
                          style={{
                            fontSize: 12,
                            color: "#6b7280",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {property.address?.street || "—"}
                        </div>
                        <div style={{ fontSize: 11, color: "#9ca3af" }}>
                          {[property.address?.city, property.address?.parish]
                            .filter(Boolean)
                            .join(", ")}
                        </div>
                      </td>
                      <td data-label="Type" style={s.td}>
                        <Pill variant={typePillVariant(property.type)}>
                          {typeLabel(property.type)}
                        </Pill>
                      </td>
                      <td data-label="Status" style={s.td}>
                        <Pill variant={statusPillVariant(property.status)}>
                          {statusLabel(property.status)}
                        </Pill>
                      </td>
                      <td data-label="Bed" style={{ ...s.td, color: isBuilding ? "#9ca3af" : "#1a1d23" }}>
                        {isBuilding ? "—" : (property.propertyDetails?.bedrooms ?? "—")}
                      </td>
                      <td data-label="Bath" style={{ ...s.td, color: isBuilding ? "#9ca3af" : "#1a1d23" }}>
                        {isBuilding ? "—" : (property.propertyDetails?.bathrooms ?? "—")}
                      </td>
                      <td data-label="Sqft" style={{ ...s.td, color: isBuilding ? "#9ca3af" : "#1a1d23" }}>
                        {isBuilding ? "—" : (property.propertyDetails?.squareFootage ?? "—")}
                      </td>
                      <td data-label="Price" style={s.td}>
                        {isRental && property.pricing?.rent && (
                          <div style={{ fontWeight: 700, color: "#1E40AF" }}>
                            {formatPrice(property.pricing.rent)}
                            <span style={{ fontWeight: 400, fontSize: 11, color: "#9ca3af" }}>
                              /mo
                            </span>
                          </div>
                        )}
                        {isSale && property.pricing?.salePrice && (
                          <div style={{ fontWeight: 700, color: "#1E40AF" }}>
                            {formatPrice(property.pricing.salePrice)}
                          </div>
                        )}
                        {isBuilding && <span style={{ color: "#9ca3af" }}>—</span>}
                      </td>
                      <td data-label="" style={{ ...s.td, textAlign: "right" }}>
                        <button
                          style={s.actBtnSm}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewDetails(property);
                          }}
                        >
                          View →
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Modals ── */}
      <PropertyFormModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        property={null}
        onPropertyUpdated={handlePropertyCreated}
        mode="create"
      />

      <UnitFormModal
        show={showUnitModal}
        onHide={handleUnitModalClose}
        unit={null}
        propertyId={createdPropertyId}
        onUnitUpdated={handleUnitCreated}
        mode="create"
        onAddAnother={handleAddAnotherUnit}
      />
    </div>
  );
}

export default Properties;
